/**
 * Gedeelde drawers voor de Marketing-module.
 *
 *   TemplatePreviewDrawer — gerenderde mail-preview (voorbeeldcontact) +
 *                           testmail versturen. Gebruikt door journey-stappen
 *                           en de campagne-detailpagina.
 *   MarketingSendDrawer   — de mail zoals de klant hem kreeg, via
 *                           /api/marketing/send-body (zelfde hardheids-
 *                           weergave als de klant-tijdlijn-drawer).
 *
 * Plan: plans/2026-08-13-marketing-journeys-inzicht-ui.md
 */
import { useEffect, useState } from 'react';
import { useAuthStore } from '../../stores/auth-store';
import { marketingFetch } from '../../stores/marketing-store';
import { fetchMarketingSendBody, type MarketingSendBody } from '../../lib/marketing-send-body';
import { formatDateTime, StatusBadge } from './shared';

// ─── Drawer-schil ───────────────────────────────────────────────

function DrawerShell({ title, subtitle, onClose, children }: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className="fixed inset-0 bg-slate-900/30 z-40" onClick={onClose} aria-hidden />
      <div className="fixed top-0 right-0 h-full w-full max-w-[680px] bg-white z-50 shadow-xl flex flex-col">
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200">
          <div className="min-w-0">
            <h2 className="font-semibold text-gray-900 truncate">{title}</h2>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5 truncate">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none px-2" aria-label="Sluiten">
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </>
  );
}

// ─── Testmail ───────────────────────────────────────────────────

function TestMailPanel({ templateId, subjectOverride }: { templateId: string; subjectOverride?: string | null }) {
  const userEmail = useAuthStore((s) => s.user?.email ?? '');
  const [to, setTo] = useState(userEmail);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  const send = async () => {
    setBusy(true);
    setResult(null);
    try {
      await marketingFetch(`/api/marketing/templates?id=${templateId}&action=test-send`, {
        method: 'POST',
        body: { to, ...(subjectOverride ? { subject_override: subjectOverride } : {}) },
      });
      setResult({ ok: true, message: `Testmail verstuurd naar ${to}` });
    } catch (err) {
      setResult({ ok: false, message: err instanceof Error ? err.message : 'Versturen mislukt' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-slate-200 px-5 py-4 bg-slate-50/50">
      <p className="text-xs font-medium text-gray-500 mb-2">
        Testmail — onderwerp krijgt [TEST]-prefix, alleen naar eigen adressen (@cincocleaning.com / @kleda.nl)
      </p>
      <div className="flex items-center gap-2">
        <input
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="naam@cincocleaning.com"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <button
          onClick={send}
          disabled={busy || !to}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 whitespace-nowrap"
        >
          {busy ? 'Versturen…' : 'Testmail versturen'}
        </button>
      </div>
      {result && (
        <p className={`text-xs mt-2 ${result.ok ? 'text-emerald-700' : 'text-red-600'}`}>{result.message}</p>
      )}
    </div>
  );
}

// ─── Template-preview ───────────────────────────────────────────

interface PreviewResponse {
  template: { id: string; key: string; name: string; version: number };
  preview: { subject: string; html: string; warnings: string[] };
}

export function TemplatePreviewDrawer({ templateId, title, subjectOverride, onClose }: {
  templateId: string;
  /** Kop van de drawer, bv. de stapnaam. */
  title: string;
  subjectOverride?: string | null;
  onClose: () => void;
}) {
  const [data, setData] = useState<PreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams({ id: templateId, preview: '1' });
    if (subjectOverride) params.set('subject_override', subjectOverride);
    marketingFetch<PreviewResponse>(`/api/marketing/templates?${params}`)
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : 'Preview laden mislukt'));
  }, [templateId, subjectOverride]);

  return (
    <DrawerShell
      title={title}
      subtitle={data ? `${data.template.name} (v${data.template.version}) · voorbeeldgegevens` : undefined}
      onClose={onClose}
    >
      {error && <div className="m-5 px-4 py-3 bg-red-50 rounded text-sm text-red-700">{error}</div>}
      {!data && !error && (
        <div className="p-6 space-y-3 animate-pulse">
          <div className="h-3 bg-slate-100 rounded w-2/3" />
          <div className="h-64 bg-slate-100 rounded mt-4" />
        </div>
      )}
      {data && (
        <div className="p-5 space-y-3">
          <dl className="grid grid-cols-[90px_1fr] gap-y-1 text-xs">
            <dt className="text-gray-400">Onderwerp</dt>
            <dd className="text-gray-800">{data.preview.subject}</dd>
          </dl>
          {data.preview.warnings.length > 0 && (
            <div className="px-3 py-2 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
              {data.preview.warnings.join(' · ')}
            </div>
          )}
          <iframe
            title="Template preview"
            srcDoc={data.preview.html}
            sandbox=""
            className="w-full h-[calc(100vh-290px)] border border-slate-200 rounded bg-white"
          />
        </div>
      )}
      <TestMailPanel templateId={templateId} subjectOverride={subjectOverride} />
    </DrawerShell>
  );
}

// ─── Verstuurde mail ────────────────────────────────────────────

export function MarketingSendDrawer({ sendId, onClose }: { sendId: string; onClose: () => void }) {
  const [body, setBody] = useState<MarketingSendBody | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setBody(null);
    fetchMarketingSendBody(sendId)
      .then(setBody)
      .catch((err) => setError(err instanceof Error ? err.message : 'Mail ophalen mislukt'));
  }, [sendId]);

  return (
    <DrawerShell
      title={body?.subject || 'Verstuurde mail'}
      subtitle={body ? `${body.toEmail ?? ''} · ${formatDateTime(body.sentAt)}` : undefined}
      onClose={onClose}
    >
      {error && <div className="m-5 px-4 py-3 bg-red-50 rounded text-sm text-red-700">{error}</div>}
      {!body && !error && (
        <div className="p-6 space-y-3 animate-pulse">
          <div className="h-3 bg-slate-100 rounded w-2/3" />
          <div className="h-64 bg-slate-100 rounded mt-4" />
        </div>
      )}
      {body && (
        <div className="p-5 space-y-3">
          <div className="flex items-center gap-2 text-xs">
            {body.status && <StatusBadge status={body.status} />}
            {body.via && <span className="text-gray-500">via {body.via}</span>}
          </div>
          {/* Hardheid expliciet — een reconstructie is geen bewijs. */}
          {body.source === 'opgeslagen' && (
            <div className="px-3 py-2 rounded bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-800">
              Exact zoals verstuurd, opgeslagen op het verzendmoment.
            </div>
          )}
          {body.source === 'reconstructie' && (
            <div className="px-3 py-2 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
              <strong>Reconstructie, geen kopie.</strong> {body.warning}
            </div>
          )}
          {body.source === 'onbeschikbaar' && (
            <div className="px-3 py-2 rounded bg-slate-50 border border-slate-200 text-[11px] text-slate-600">
              {body.warning || 'De inhoud van deze mail is niet beschikbaar.'}
            </div>
          )}
          {body.html ? (
            <iframe
              title="Verstuurde marketing-mail"
              srcDoc={body.html}
              sandbox=""
              className="w-full h-[calc(100vh-260px)] border border-slate-200 rounded bg-white"
            />
          ) : body.text ? (
            <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">{body.text}</pre>
          ) : (
            <p className="text-sm text-slate-400 italic">Geen body beschikbaar.</p>
          )}
        </div>
      )}
    </DrawerShell>
  );
}

export { TestMailPanel };
